/**
 * Web API Client
 * ==============
 *
 * HTTP client for communicating with Auto-Claude backend API.
 * Replaces Electron IPC with REST/WebSocket calls.
 */

// Configurable API base URL
const getApiBaseUrl = (): string => {
  // Check for environment variable or use relative path
  if (typeof window !== 'undefined') {
    // In browser, use relative URL (same origin) or configured URL
    return (window as any).__AUTO_CLAUDE_API_URL__ || '/api';
  }
  return '/api';
};

const getWsBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}`;
  }
  return 'ws://localhost:3000';
};

export const API_BASE_URL = getApiBaseUrl();
export const WS_BASE_URL = getWsBaseUrl();

/**
 * Standard API response wrapper
 */
export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * HTTP request options
 */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * Make an API request
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<APIResponse<T>> {
  const { method = 'GET', body, headers = {}, timeout = 30000 } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.detail || data.error || `HTTP ${response.status}`,
      };
    }

    return {
      success: true,
      data: data as T,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timeout' };
      }
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Unknown error' };
  }
}

// =============================================================================
// Terminal API
// =============================================================================

export interface TerminalSession {
  id: string;
  pid: number;
  cwd: string;
  created_at: string;
  title: string;
  cols: number;
  rows: number;
  is_claude_active: boolean;
  claude_session_id: string | null;
}

export interface CreateTerminalOptions {
  cwd?: string;
  cols?: number;
  rows?: number;
  shell?: string;
}

export const terminalApi = {
  /**
   * Create a new terminal session
   */
  create: (options: CreateTerminalOptions = {}) =>
    apiRequest<TerminalSession>('/terminals', {
      method: 'POST',
      body: options,
    }),

  /**
   * List all terminal sessions
   */
  list: () =>
    apiRequest<{ sessions: TerminalSession[]; count: number }>('/terminals'),

  /**
   * Get terminal session info
   */
  get: (sessionId: string) =>
    apiRequest<TerminalSession>(`/terminals/${sessionId}`),

  /**
   * Destroy a terminal session
   */
  destroy: (sessionId: string) =>
    apiRequest(`/terminals/${sessionId}`, { method: 'DELETE' }),

  /**
   * Resize terminal
   */
  resize: (sessionId: string, cols: number, rows: number) =>
    apiRequest(`/terminals/${sessionId}/resize`, {
      method: 'POST',
      body: { cols, rows },
    }),

  /**
   * Invoke Claude in terminal
   */
  invokeClaude: (sessionId: string, resumeSession?: string) =>
    apiRequest(`/terminals/${sessionId}/claude`, {
      method: 'POST',
      body: { resume_session: resumeSession },
    }),

  /**
   * Set terminal title
   */
  setTitle: (sessionId: string, title: string) =>
    apiRequest(`/terminals/${sessionId}/title?title=${encodeURIComponent(title)}`, {
      method: 'POST',
    }),
};

// =============================================================================
// Files API
// =============================================================================

export interface FileEntry {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size: number;
  modified: string;
  permissions: string;
  is_hidden: boolean;
  extension?: string;
  mime_type?: string;
}

export interface DirectoryListing {
  path: string;
  entries: FileEntry[];
  parent: string | null;
  count: number;
}

export interface FileContent {
  path: string;
  content: string;
  encoding: string;
  size: number;
  modified: string;
  mime_type: string | null;
}

export interface FileStat {
  path: string;
  exists: boolean;
  type: 'file' | 'directory' | null;
  size: number | null;
  created: string | null;
  modified: string | null;
  accessed: string | null;
  permissions: string | null;
  is_readable: boolean;
  is_writable: boolean;
}

export const filesApi = {
  /**
   * List directory contents
   */
  list: (path: string = '', showHidden: boolean = false) =>
    apiRequest<DirectoryListing>(
      `/files?path=${encodeURIComponent(path)}&show_hidden=${showHidden}`
    ),

  /**
   * Read file content
   */
  read: (path: string, encoding: string = 'utf-8') =>
    apiRequest<FileContent>(
      `/files/content?path=${encodeURIComponent(path)}&encoding=${encoding}`
    ),

  /**
   * Write file content
   */
  write: (path: string, content: string, createDirs: boolean = true) =>
    apiRequest('/files/content', {
      method: 'PUT',
      body: { path, content, create_dirs: createDirs },
    }),

  /**
   * Create directory
   */
  mkdir: (path: string, parents: boolean = true) =>
    apiRequest(`/files/mkdir?path=${encodeURIComponent(path)}&parents=${parents}`, {
      method: 'POST',
    }),

  /**
   * Delete file or directory
   */
  delete: (path: string, recursive: boolean = false) =>
    apiRequest(`/files?path=${encodeURIComponent(path)}&recursive=${recursive}`, {
      method: 'DELETE',
    }),

  /**
   * Rename/move file
   */
  rename: (oldPath: string, newPath: string) =>
    apiRequest('/files/rename', {
      method: 'POST',
      body: { old_path: oldPath, new_path: newPath },
    }),

  /**
   * Get file stats
   */
  stat: (path: string) =>
    apiRequest<FileStat>(`/files/stat?path=${encodeURIComponent(path)}`),
};

// =============================================================================
// Settings API
// =============================================================================

export interface Settings {
  theme: string;
  editor: {
    fontSize: number;
    fontFamily: string;
    tabSize: number;
    wordWrap: boolean;
    lineNumbers: boolean;
    minimap: boolean;
  };
  terminal: {
    fontSize: number;
    fontFamily: string;
    cursorStyle: string;
    cursorBlink: boolean;
    scrollback: number;
  };
  claude: {
    autoLaunch: boolean;
    defaultModel: string;
    resumeLastSession: boolean;
  };
  files: {
    showHidden: boolean;
    excludePatterns: string[];
  };
}

export const settingsApi = {
  /**
   * Get all settings
   */
  getAll: () => apiRequest<{ settings: Settings; path: string }>('/settings'),

  /**
   * Update settings (merge)
   */
  update: (settings: Partial<Settings>) =>
    apiRequest<{ settings: Settings; path: string }>('/settings', {
      method: 'PUT',
      body: { settings },
    }),

  /**
   * Get a specific setting
   */
  get: (key: string) =>
    apiRequest<{ key: string; value: unknown }>(`/settings/${key}`),

  /**
   * Set a specific setting
   */
  set: (key: string, value: unknown) =>
    apiRequest(`/settings/${key}`, {
      method: 'PUT',
      body: { value },
    }),

  /**
   * Delete a setting
   */
  delete: (key: string) =>
    apiRequest(`/settings/${key}`, { method: 'DELETE' }),

  /**
   * Reset to defaults
   */
  reset: () =>
    apiRequest<{ settings: Settings; path: string }>('/settings/reset', {
      method: 'POST',
    }),
};

// =============================================================================
// Projects API
// =============================================================================

export interface ProjectMetadata {
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  tags: string[];
  default_model: string | null;
  spec_file: string | null;
  git_repo: string | null;
}

export interface ProjectInfo {
  name: string;
  path: string;
  exists: boolean;
  has_metadata: boolean;
  metadata: ProjectMetadata | null;
  file_count: number | null;
  last_modified: string | null;
  has_git: boolean;
  has_spec: boolean;
}

export interface ClaudeSession {
  session_id: string;
  created_at: string;
  last_message: string | null;
  message_count: number;
}

export const projectsApi = {
  /**
   * List all projects
   */
  list: (includeDetails: boolean = true) =>
    apiRequest<{ projects: ProjectInfo[]; count: number }>(
      `/projects?include_details=${includeDetails}`
    ),

  /**
   * Create a new project
   */
  create: (options: {
    name: string;
    description?: string;
    tags?: string[];
    initialize_git?: boolean;
    create_readme?: boolean;
  }) =>
    apiRequest<ProjectInfo>('/projects', {
      method: 'POST',
      body: options,
    }),

  /**
   * Get project details
   */
  get: (name: string) => apiRequest<ProjectInfo>(`/projects/${name}`),

  /**
   * Update project metadata
   */
  update: (
    name: string,
    updates: {
      description?: string;
      tags?: string[];
      default_model?: string;
      spec_file?: string;
    }
  ) =>
    apiRequest<ProjectInfo>(`/projects/${name}`, {
      method: 'PUT',
      body: updates,
    }),

  /**
   * Delete project
   */
  delete: (name: string, deleteFiles: boolean = false) =>
    apiRequest(`/projects/${name}?delete_files=${deleteFiles}`, {
      method: 'DELETE',
    }),

  /**
   * List Claude sessions for project
   */
  listSessions: (name: string) =>
    apiRequest<{ project: string; sessions: ClaudeSession[]; count: number }>(
      `/projects/${name}/sessions`
    ),
};

// =============================================================================
// Health API
// =============================================================================

export const healthApi = {
  /**
   * Check API health
   * Note: Health endpoint is at root level, not under /api
   */
  check: async (): Promise<APIResponse<{ status: string }>> => {
    try {
      const response = await fetch('/health');
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}` };
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },
};
